import { dateOnlyOf } from '@/lib/utils/custom-date/custom-date';
import { hrTermOn } from '@/lib/utils/work-days/work-days';
import { CountedLeave, leaveDaysOf } from '@/modules/time-off/team-time-off/utils/leave-days';
import { WorkDayInfo } from '../types/employee.types';

/** A Pending or Approved leave of the employee, as the HR Info tab checks it. */
export interface OpenLeave extends CountedLeave {
  ItemId: string;
  LeaveCategory?: string;
  Status: string;
}

/** An HR term as saved (`HrTermRecord`) or as the HR Info tab submits it (`HrTermInput`). */
export interface TermWorkDays {
  IsCurrentHrYear?: boolean;
  HrPeriodStartDate?: string;
  HrPeriodEndDate?: string;
  WorkDayHours: WorkDayInfo[];
}

/** A leave whose day count a work-day change moves; `cancel`: it is cancelled (LEAVE_REQUEST_CANCEL). */
export interface LeaveDayChange {
  leave: OpenLeave;
  before: number;
  after: number;
  cancel: boolean;
}

const asDatedTerms = (terms: readonly TermWorkDays[]) =>
  terms.map((term) => ({
    HRPeriodStartDate: term.HrPeriodStartDate,
    HRPeriodEndDate: term.HrPeriodEndDate,
    IsCurrentHrYear: term.IsCurrentHrYear,
    WorkDays: term.WorkDayHours,
  }));

/**
 * The open leaves (`leaves`: Pending and Approved, not over yet) whose days a save of the HR terms changes: each
 * counted (`leaveDaysOf`, the term in force on its first day) with the terms `before` and `after` the save. A
 * leave that shrinks just counts less. An Approved one that grows is cancelled (`cancel`) while it has not started
 * (`today`, the organization's day, at most its first day), an overtime leave with its taken hours back; the
 * employee requests it again. A Pending one that grows waits for its reviewer, who sees the new count.
 */
export const leaveDayChangesOf = ({
  leaves,
  before,
  after,
  holidays,
  today,
}: {
  leaves: readonly OpenLeave[];
  before: readonly TermWorkDays[];
  after: readonly TermWorkDays[];
  holidays: ReadonlySet<string>;
  today: string;
}): LeaveDayChange[] => {
  const oldTerms = asDatedTerms(before);
  const newTerms = asDatedTerms(after);
  const changes: LeaveDayChange[] = [];
  for (const leave of leaves) {
    const start = dateOnlyOf(leave.StartDate);
    const count = (terms: typeof oldTerms) =>
      leaveDaysOf(leave, hrTermOn(terms, start)?.WorkDays ?? [], holidays);
    const was = count(oldTerms);
    const now = count(newTerms);
    if (was === now) continue;
    changes.push({
      leave,
      before: was,
      after: now,
      cancel: now > was && leave.Status === 'Approved' && today <= start,
    });
  }
  return changes;
};
